import { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import { Button, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { inkBounds, padBounds } from '../model/ink-bounds.js';

/** Chizish maydonining ichki o'lchami — CSS o'lchamidan ayri (Retina uchun). */
const WIDTH = 600;
const HEIGHT = 200;
const LINE_WIDTH = 2.5;
/** Qirqilgan imzo atrofidagi nafas, ichki piksellarda. */
const PADDING = 8;

/**
 * IMZO CHIZISH MAYDONI.
 *
 * Sichqoncha ham, barmoq ham bir xil ishlaydi (`pointer` hodisalari): ekran
 * ko'pincha telefonda ochiladi va u yerda imzo barmoq bilan chiziladi.
 *
 * Fon SHAFFOF qoldiriladi va bu ataylab: rasm blankdagi katakning ustiga
 * tushadi, oq to'rtburchak esa katakning chiziqlarini yopib qo'yardi.
 *
 * Natija siyoh chegarasi bo'yicha QIRQILADI (`inkBounds`) — odam katak
 * o'rtasiga kichik chizadi va bo'sh chetlari bilan saqlansa, hujjatdagi
 * imzo mitti bo'lib qolardi.
 */
export const SignaturePad = ({
  onDone,
  onSkip,
  busy = false,
  skipLabel,
}: {
  /** Chizilgan imzo PNG sifatida. */
  onDone: (file: File) => void;
  /** «Keyinroq» — imzosiz davom etish. Berilmasa tugma chiqmaydi. */
  onSkip?: () => void;
  busy?: boolean;
  skipLabel?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  /**
   * 2D kontekst — OLINMASA `null`.
   *
   * `getContext` har muhitda ham ishlamaydi (sinov muhiti, canvas o'chirilgan
   * brauzer) va u yerda ISTISNO tashlaydi. Tashlangan istisno butun dialogni
   * yiqitardi — imzo esa qo'shimcha qulaylik va uning yo'qligi qabul qilishga
   * xalaqit bermasligi kerak.
   */
  const context = (): CanvasRenderingContext2D | null => {
    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvasRef.current?.getContext('2d') ?? null;
    } catch {
      return null;
    }
    if (ctx === null) return null;
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    return ctx;
  };

  /** Hodisa nuqtasini CANVAS koordinatasiga o'giradi (CSS o'lchami boshqa). */
  const pointOf = (event: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const canvas = canvasRef.current;
    if (canvas === null) return [0, 0];
    const rect = canvas.getBoundingClientRect();
    return [
      ((event.clientX - rect.left) / rect.width) * WIDTH,
      ((event.clientY - rect.top) / rect.height) * HEIGHT,
    ];
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const ctx = context();
    if (ctx === null) return;
    // Barmoq maydondan chiqib ketsa ham chiziq uzilmasin.
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    const [x, y] = pointOf(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!drawing.current) return;
    const ctx = context();
    if (ctx === null) return;
    const [x, y] = pointOf(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const end = (): void => {
    drawing.current = false;
  };

  const clear = useCallback((): void => {
    try {
      canvasRef.current?.getContext('2d')?.clearRect(0, 0, WIDTH, HEIGHT);
    } catch {
      // Kontekst yo'q — tozalaydigan narsa ham yo'q.
    }
    setHasInk(false);
  }, []);

  useEffect(() => clear(), [clear]);

  /**
   * Imzoni PNG faylga o'giradi — siyoh chegarasi bo'yicha qirqib.
   *
   * Qirqish IKKINCHI canvas da bajariladi: birinchisiga tegilmaydi, ya'ni
   * saqlagandan keyin ham chizgan narsasi ekranda turaveradi va odam nima
   * yuborganini ko'rib turadi.
   */
  const save = (): void => {
    const canvas = canvasRef.current;
    const ctx = context();
    if (canvas === null || ctx === null) return;

    const image = ctx.getImageData(0, 0, WIDTH, HEIGHT);
    const found = inkBounds(image.data, WIDTH, HEIGHT);
    if (found === null) return;

    const box = padBounds(found, PADDING, WIDTH, HEIGHT);
    const out = document.createElement('canvas');
    out.width = box.width;
    out.height = box.height;
    out
      .getContext('2d')
      ?.drawImage(canvas, box.x, box.y, box.width, box.height, 0, 0, box.width, box.height);

    out.toBlob((blob) => {
      if (blob === null) return;
      onDone(new File([blob], 'imzo.png', { type: 'image/png' }));
    }, 'image/png');
  };

  return (
    <div className="flex flex-col gap-2">
      <canvas
        ref={canvasRef}
        width={WIDTH}
        height={HEIGHT}
        aria-label={t('signature.padLabel')}
        role="img"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        className={cn(
          'h-32 w-full rounded-[var(--radius-md)] border border-dashed border-border-strong',
          'bg-surface',
          // Barmoq bilan chizilganda sahifa SURILIB ketmasligi kerak.
          'touch-none',
        )}
      />

      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={clear} disabled={!hasInk || busy}>
          <Eraser size={14} aria-hidden />
          {t('signature.clear')}
        </Button>

        <div className="flex items-center gap-2">
          {onSkip === undefined ? null : (
            <Button variant="ghost" size="sm" onClick={onSkip} disabled={busy}>
              {skipLabel ?? t('signature.later')}
            </Button>
          )}
          <Button size="sm" onClick={save} disabled={!hasInk || busy}>
            {t('signature.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};
